// ── src/lib/supabase.ts ──
// 本地 API 墊片層：模擬 Supabase 客戶端介面，底層全部改道至本地 Node API。
// 前端 154 處呼叫點零改動，自動透過此墊片連接本地後端。

// 預設空字串 = 同源相對路徑（VPS 部署時前後端同網域）；本地 dev 由 .env 的 VITE_API_URL 指定
const API_URL = import.meta.env.VITE_API_URL || "";

// ── Token 儲存（統一入口：src/lib/authToken.ts）──
import { getToken, setToken } from "./authToken";

// ── Auth 狀態監聽器 ──
type AuthCallback = (event: string, session: Session | null) => void;
const authListeners: Set<AuthCallback> = new Set();

function notifyAuth(event: string, session: Session | null) {
  for (const cb of authListeners) {
    try { cb(event, session); } catch { /* ignore */ }
  }
}

// ── Session 形狀（與 Supabase 相容，含 app_metadata 以對齊 User type） ──
export interface User {
  id: string;
  email: string;
  user_metadata: Record<string, unknown>;
  app_metadata: Record<string, unknown>;
  aud?: string;
  created_at?: string;
}

export interface Session {
  access_token: string;
  user: User;
}

// ── HTTP 助手 ──
function authHeaders(): Record<string, string> {
  const token = getToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

async function apiPost(path: string, body?: unknown): Promise<{ data: any; error: { message: string } | null }> {
  try {
    const resp = await fetch(`${API_URL}${path}`, {
      method: "POST",
      headers: authHeaders(),
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    const json = await resp.json().catch(() => ({})) as Record<string, unknown>;
    if (!resp.ok) {
      const msg = (json.message as string) || (json.error as string) || `HTTP ${resp.status}`;
      return { data: null, error: { message: msg } };
    }
    return { data: json, error: null };
  } catch (err) {
    return { data: null, error: { message: err instanceof Error ? err.message : "Network error" } };
  }
}

async function apiGet(path: string): Promise<{ data: any; error: { message: string } | null }> {
  try {
    const resp = await fetch(`${API_URL}${path}`, {
      method: "GET",
      headers: authHeaders(),
    });
    const json = await resp.json().catch(() => ({})) as Record<string, unknown>;
    if (!resp.ok) {
      const msg = (json.message as string) || (json.error as string) || `HTTP ${resp.status}`;
      return { data: null, error: { message: msg } };
    }
    return { data: json, error: null };
  } catch (err) {
    return { data: null, error: { message: err instanceof Error ? err.message : "Network error" } };
  }
}

// ── 查詢結果形狀（data 用 any 與 Supabase SDK 行為一致） ──
export interface QueryResult {
  data: any;
  error: { message: string } | null;
  count?: number | null;
}

// ── 查詢建造器（模擬 Supabase PostgrestFilterBuilder） ──
interface FilterOp { op: string; col: string; val: unknown }
interface OrderOp { column: string; ascending: boolean }

class QueryBuilder {
  private _table: string;
  private _op: "select" | "insert" | "update" | "delete" | "upsert" = "select";
  private _selectStr = "*";
  private _count: string | null = null;
  private _head = false;
  private _filters: FilterOp[] = [];
  private _order: OrderOp[] = [];
  private _limit: number | null = null;
  private _offset: number | null = null;
  private _payload: unknown = null;
  private _onConflict: string | null = null;
  private _ignoreDuplicates = false;
  private _single = false;
  private _maybeSingle = false;
  private _returning = false;

  constructor(table: string) {
    this._table = table;
  }

  select(cols = "*", opts?: { count?: string; head?: boolean }): this {
    // insert/update/delete/upsert 後呼叫 select() = 要求回傳修改後的行
    if (this._op !== "select") {
      this._returning = true;
    }
    this._selectStr = cols;
    if (opts?.count) this._count = opts.count;
    if (opts?.head) this._head = opts.head;
    return this;
  }

  insert(payload: unknown): this {
    this._op = "insert";
    this._payload = payload;
    return this;
  }

  upsert(payload: unknown, opts?: { onConflict?: string; ignoreDuplicates?: boolean }): this {
    this._op = "upsert";
    this._payload = payload;
    this._onConflict = opts?.onConflict ?? null;
    this._ignoreDuplicates = opts?.ignoreDuplicates ?? false;
    return this;
  }

  update(payload: unknown): this {
    this._op = "update";
    this._payload = payload;
    return this;
  }

  delete(): this {
    this._op = "delete";
    return this;
  }

  eq(col: string, val: unknown): this { this._filters.push({ op: "eq", col, val }); return this; }
  neq(col: string, val: unknown): this { this._filters.push({ op: "neq", col, val }); return this; }
  gt(col: string, val: unknown): this { this._filters.push({ op: "gt", col, val }); return this; }
  gte(col: string, val: unknown): this { this._filters.push({ op: "gte", col, val }); return this; }
  lt(col: string, val: unknown): this { this._filters.push({ op: "lt", col, val }); return this; }
  lte(col: string, val: unknown): this { this._filters.push({ op: "lte", col, val }); return this; }
  ilike(col: string, val: string): this { this._filters.push({ op: "ilike", col, val }); return this; }
  like(col: string, val: string): this { this._filters.push({ op: "like", col, val }); return this; }
  is(col: string, val: unknown): this { this._filters.push({ op: "is", col, val }); return this; }
  in(col: string, vals: unknown[]): this { this._filters.push({ op: "in", col, val: vals }); return this; }
  or(expr: string): this { this._filters.push({ op: "or", col: "", val: expr }); return this; }

  order(col: string, opts?: { ascending?: boolean }): this {
    this._order.push({ column: col, ascending: opts?.ascending !== false });
    return this;
  }

  limit(n: number): this { this._limit = n; return this; }

  range(from: number, to: number): this {
    this._offset = from;
    this._limit = to - from + 1;
    return this;
  }

  single(): this { this._single = true; return this; }
  maybeSingle(): this { this._maybeSingle = true; return this; }

  private async execute(): Promise<QueryResult> {
    const spec = {
      table: this._table,
      op: this._op,
      selectStr: this._selectStr,
      count: this._count,
      head: this._head,
      filters: this._filters,
      order: this._order,
      limit: this._limit,
      offset: this._offset,
      payload: this._payload,
      onConflict: this._onConflict,
      ignoreDuplicates: this._ignoreDuplicates,
      returning: this._returning,
    };
    const result = await apiPost(`/api/tables/${this._table}`, spec);
    if (result.error) return { data: null, error: result.error, count: null };
    const json = result.data as { data?: unknown; count?: number };
    let data = json.data;
    // Supabase 語義：single() 期望恰好 1 行；maybeSingle() 期望 0 或 1 行
    if (this._single || this._maybeSingle) {
      if (Array.isArray(data)) {
        if (this._single && data.length !== 1) {
          return {
            data: null,
            error: { message: data.length === 0 ? "No rows found" : "Multiple rows found" },
            count: json.count,
          };
        }
        data = data[0] ?? null;
      }
    }
    return { data, error: null, count: json.count };
  }

  // thenable：讓 await supabase.from(...) 直接可用
  then<TResult1 = QueryResult, TResult2 = never>(
    onfulfilled?: ((value: QueryResult) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | null
  ): Promise<TResult1 | TResult2> {
    return this.execute().then(onfulfilled, onrejected);
  }
}

// ── Channel（模擬 Supabase Realtime，底層走 WebSocket） ──
interface ChannelFilter {
  event?: string;
  schema?: string;
  table: string;
  filter?: string;
}

type ChannelCallback = (payload: {
  new: any;
  old?: any;
  event?: string;
}) => void;

class Channel {
  readonly name: string;
  private _handlers: { filter: ChannelFilter; callback: ChannelCallback }[] = [];
  private _ws: WebSocket | null = null;
  private _subscribed = false;
  private _pingTimer: ReturnType<typeof setInterval> | null = null;

  constructor(name: string) {
    this.name = name;
  }

  on(event: string, filter: ChannelFilter, callback: ChannelCallback): this {
    this._handlers.push({ filter: { ...filter, event }, callback });
    return this;
  }

  subscribe(): this {
    if (this._subscribed) return this;
    this._subscribed = true;
    // WebSocket 需要絕對 URL：有 VITE_API_URL 就換成 ws://，否則依目前頁面同源組裝
    const wsUrl = API_URL
      ? API_URL.replace(/^http/, "ws") + "/ws"
      : (location.protocol === "https:" ? "wss://" : "ws://") + location.host + "/ws";
    try {
      this._ws = new WebSocket(wsUrl);
      this._ws.onmessage = (ev: MessageEvent) => {
        try {
          const msg = JSON.parse(ev.data as string) as {
            event: string;
            table: string;
            new: Record<string, unknown>;
            old?: Record<string, unknown>;
          };
          for (const h of this._handlers) {
            if (h.filter.table !== msg.table) continue;
            if (h.filter.event && h.filter.event !== msg.event) continue;
            if (h.filter.filter) {
              // 解析 "conversation_id=eq.xxx" 格式
              const m = h.filter.filter.match(/^([a-z_]+)=eq\.(.+)$/i);
              if (m) {
                const [, col, val] = m;
                if (String((msg.new || {})[col]) !== val) continue;
              }
            }
            h.callback({ new: msg.new, old: msg.old, event: msg.event });
          }
        } catch { /* ignore */ }
      };
      // 保活 ping（每 25 秒）
      this._pingTimer = setInterval(() => {
        if (this._ws?.readyState === WebSocket.OPEN) {
          this._ws.send(JSON.stringify({ type: "ping" }));
        }
      }, 25000);
    } catch { /* ignore */ }
    return this;
  }

  unsubscribe() {
    this._subscribed = false;
    if (this._pingTimer) {
      clearInterval(this._pingTimer);
      this._pingTimer = null;
    }
    if (this._ws) {
      this._ws.close();
      this._ws = null;
    }
  }
}

// ── Session 實作（抽出避免循環引用） ──
async function getSessionImpl(): Promise<{ data: { session: Session | null }; error: null }> {
  const token = getToken();
  if (!token) return { data: { session: null }, error: null };
  const { data, error } = await apiGet("/api/auth/session");
  if (error || !data) return { data: { session: null }, error: null };
  const d = data as { session: Session | null };
  return { data: { session: d.session }, error: null };
}

// ── 主客戶端（與 Supabase 客戶端介面完全相容） ──
export const supabase = {
  auth: {
    async signInWithPassword({ email, password }: { email: string; password: string }) {
      const { data, error } = await apiPost("/api/auth/signin", { email, password });
      if (error) return { data: { session: null, user: null }, error };
      const d = data as { access_token: string; user: User };
      // 確保 user 含 app_metadata（Supabase User type 必需）
      const user: User = { ...d.user, app_metadata: d.user.app_metadata || {} };
      setToken(d.access_token);
      const session: Session = { access_token: d.access_token, user };
      notifyAuth("SIGNED_IN", session);
      return { data: { session, user }, error: null };
    },

    async getSession() {
      return getSessionImpl();
    },

    async getUser(_token?: string) {
      const { data: sessData } = await getSessionImpl();
      return { data: { user: sessData.session?.user ?? null }, error: null };
    },

    async signOut() {
      setToken(null);
      notifyAuth("SIGNED_OUT", null);
      return { error: null };
    },

    async updateUser(payload: { password?: string; email?: string }) {
      const { error } = await apiPost("/api/auth/update-user", payload);
      return { data: { user: null }, error };
    },

    async resend(opts: { type: string; email: string }) {
      const { error } = await apiPost("/api/auth/resend", { email: opts.email });
      return { data: {}, error };
    },

    onAuthStateChange(callback: AuthCallback) {
      authListeners.add(callback);
      return {
        data: {
          subscription: {
            unsubscribe: () => { authListeners.delete(callback); },
          },
        },
      };
    },
  },

  from(table: string): QueryBuilder {
    return new QueryBuilder(table);
  },

  channel(name: string): Channel {
    return new Channel(name);
  },

  removeChannel(channel: Channel) {
    channel.unsubscribe();
  },

  functions: {
    async invoke(name: string, opts?: { body?: unknown }) {
      const { data, error } = await apiPost(`/api/functions/${name}`, opts?.body ?? {});
      return { data, error };
    },
  },
};
